import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import BookManager from "../../services/dashboard/BookManager";
import SubscriptionManager from "../../services/dashboard/SubscriptionManager";
import OrderManager from "../../services/dashboard/OrderManager";
import UserManager from "../../services/dashboard/UserManager";
import UserSubscriptionPie from "./UserSubscriptionPie";
import PlanBreakdownPie from "./PlanBreakdownPie";
import PlanPackageBreakdownPie from "./PlanPackageBreakdownPie";
import MostRentedBooks from "./MostRentedBooks";
import RevenueStats from "./RevenueStats";
import {
  Users, BookOpen, ClipboardList, CornerUpLeft, 
  TrendingUp, TrendingDown, AlertCircle, Package, ArrowUpRight,
  Plus, UserPlus, Eye, UserCheck
} from "lucide-react";

export default function DashboardHome() {
  const [totalBooks, setTotalBooks] = useState(null);
  const [totalUsers, setTotalUsers] = useState(null);
  const [totalSubscribers, setTotalSubscribers] = useState(null);
  const [booksWithUsersCount, setBooksWithUsersCount] = useState(null);
  const [expiringSubscribersCount, setExpiringSubscribersCount] = useState(null);
  const [pendingBookRequests, setPendingBookRequests] = useState(null);
  const [pendingBookReturnRequests, setPendingBookReturnRequests] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [planCounts, setPlanCounts] = useState([]);
  const [planPackageCounts, setPlanPackageCounts] = useState([]);
  const [expiredSubscribersCount, setExpiredSubscribersCount] = useState(null);

  const role = useSelector(state => {
    const roles = state.adminAuth.admin?.roles;
    if (!roles) return "";
    if (Array.isArray(roles)) {
      const first = roles[0];
      if (typeof first === "string") return first;
      if (first && typeof first.name === "string") return first.name;
    }
    return state.adminAuth.admin?.role || "";
  });

  const admin = useSelector((state) => state.adminAuth.admin);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchDashboardStats() {
      try {
        const booksTotal = await BookManager.getBookCount();
        setTotalBooks(booksTotal);

        const subscribersArray = await SubscriptionManager.getAllSubscribedUsersWithDetails();
        const subs = Array.isArray(subscribersArray) ? subscribersArray : [];
        setTotalSubscribers(subs.length);

        const userArray = await UserManager.getAllUsers();
        const users = Array.isArray(userArray) ? userArray : [];
        setTotalUsers(users.length);

        const planMap = subs.reduce((acc, sub) => {
          const plan = sub.planName || "Unknown";
          acc[plan] = (acc[plan] || 0) + 1;
          return acc;
        }, {});
        const planCountsArr = Object.entries(planMap).map(([name, value]) => ({
          name,
          value,
        }));
        setPlanCounts(planCountsArr);

        const planPackageMap = subs.reduce((acc, sub) => {
          const key = `${sub.planName} – ${sub.packageType}`;
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {});
        const planPackageArr = Object.entries(planPackageMap).map(
          ([name, value]) => ({
            name,
            value,
          })
        );
        setPlanPackageCounts(planPackageArr);

        const expiringCount = subs.filter((u) => u.remainingDays < 7).length;
        setExpiringSubscribersCount(expiringCount);

        const expiredUsers = await SubscriptionManager.getAllExpiredSubscribedUsersWithDetails();
        setExpiredSubscribersCount(Array.isArray(expiredUsers) ? expiredUsers.length : 0);

        const pendingOrdersArray = await OrderManager.getPendingOrders();
        setPendingBookRequests(
          Array.isArray(pendingOrdersArray) ? pendingOrdersArray.length : 0
        );

        const pendingRequestArray = await OrderManager.getReturnOrders();
        setPendingBookReturnRequests(
          Array.isArray(pendingRequestArray) ? pendingRequestArray.length : 0
        );

        const withMeArray = await OrderManager.getBooksWithUsers();
        setBooksWithUsersCount(
          Array.isArray(withMeArray) ? withMeArray.length : 0
        );
      } catch (err) {
        console.error("Error fetching dashboard stats:", err);
        setTotalUsers(0);
        setTotalBooks(0);
        setTotalSubscribers(0);
        setPlanCounts([]);
        setPlanPackageCounts([]);
        setExpiringSubscribersCount(0);
        setPendingBookRequests(0);
        setPendingBookReturnRequests(0);
        setBooksWithUsersCount(0);
      } finally {
        setIsLoading(false);
      }
    }

    fetchDashboardStats();
  }, []);

  const getDisplayValue = (value) => (isLoading ? "—" : value ?? 0);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  const kpiCards = [
    {
      label: "Total Users",
      value: getDisplayValue(totalUsers),
      icon: <UserCheck size={18} />,
      trend: "+15%",
      trendUp: true,
      onClick: () => navigate("/user-manager"),
      priority: "normal",
    },
    {
      label: "Active Subscribers",
      value: getDisplayValue(totalSubscribers),
      icon: <Users size={18} />,
      trend: "+12%",
      trendUp: true,
      onClick: () => navigate("/subscribed-users"),
      priority: "normal",
    },
    {
      label: "Books in Circulation",
      value: getDisplayValue(booksWithUsersCount),
      icon: <BookOpen size={18} />,
      trend: "+8%",
      trendUp: true,
      onClick: () => navigate("/bookswithusers"),
      priority: "normal",
    },
    {
      label: "Pending Orders",
      value: getDisplayValue(pendingBookRequests),
      icon: <ClipboardList size={18} />,
      trend: "-3%",
      trendUp: false,
      onClick: () => navigate("/adminOrderRequests"),
      priority: "alert",
      alertLevel: "medium",
      hasAlert: pendingBookRequests > 0,
    },
    {
      label: "Returns Due",
      value: getDisplayValue(pendingBookReturnRequests),
      icon: <CornerUpLeft size={18} />,
      trend: "+5%",
      trendUp: true,
      onClick: () => navigate("/adminReturnRequests"),
      priority: "alert",
      alertLevel: "high",
      hasAlert: pendingBookReturnRequests > 0,
    },
  ];

  return (
    <div className="space-y-6 max-w-[1600px]">
      {/* Hero Section with Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4"
      >
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {getGreeting()}, {admin?.name || "Admin"} 👋
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
            {(pendingBookRequests > 0 || pendingBookReturnRequests > 0) && (
              <span className="ml-2 text-gray-700 dark:text-gray-300">
                • You have{" "}
                {pendingBookRequests > 0 && (
                  <span className="font-medium text-orange-600 dark:text-orange-400">
                    {pendingBookRequests} pending order{pendingBookRequests !== 1 ? 's' : ''}
                  </span>
                )}
                {pendingBookRequests > 0 && pendingBookReturnRequests > 0 && ' and '}
                {pendingBookReturnRequests > 0 && (
                  <span className="font-medium text-red-600 dark:text-red-400">
                    {pendingBookReturnRequests} return{pendingBookReturnRequests !== 1 ? 's' : ''} due
                  </span>
                )}
              </span>
            )}
          </p>
          
          {/* Key Alerts */}
          {(expiringSubscribersCount > 0 || pendingBookRequests > 0 || pendingBookReturnRequests > 0) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {pendingBookReturnRequests > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-md text-xs text-red-700 dark:text-red-400">
                  <AlertCircle size={12} />
                  <span>{pendingBookReturnRequests} return{pendingBookReturnRequests !== 1 ? 's' : ''} due</span>
                </div>
              )}
              {pendingBookRequests > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 rounded-md text-xs text-orange-700 dark:text-orange-400">
                  <AlertCircle size={12} />
                  <span>{pendingBookRequests} pending</span>
                </div>
              )}
              {expiringSubscribersCount > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-md text-xs text-amber-700 dark:text-amber-400">
                  <AlertCircle size={12} />
                  <span>{expiringSubscribersCount} expiring soon</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        {role !== "ROLE_EDITOR" && (
          <div className="flex flex-wrap gap-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/bookForm")}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-md"
            >
              <Plus size={16} />
              Add Book
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/user-manager")}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-sm"
            >
              <UserPlus size={16} />
              Add User
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/adminOrderRequests")}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-sm"
            >
              <Eye size={16} />
              View Orders
            </motion.button>
          </div>
        )}
      </motion.div>

      {/* KPI Strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {kpiCards.map((card, index) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            whileHover={{ scale: 1.01, y: -2 }}
            onClick={card.onClick}
            className={`group relative rounded-xl p-6 cursor-pointer transition-all duration-200 ${
              card.hasAlert && card.alertLevel === "high"
                ? "bg-gradient-to-br from-red-50 via-orange-50 to-white dark:from-red-500/5 dark:via-orange-500/5 dark:to-gray-900 border border-red-200 dark:border-red-800/50 hover:shadow-lg hover:border-red-300 dark:hover:border-red-700/50"
                : card.hasAlert && card.alertLevel === "medium"
                ? "bg-gradient-to-br from-orange-50 to-white dark:from-orange-500/5 dark:to-gray-900 border border-orange-200 dark:border-orange-800/50 hover:shadow-lg hover:border-orange-300 dark:hover:border-orange-700/50"
                : "bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 hover:shadow-md"
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`p-2 rounded-lg transition-colors ${
                card.hasAlert && card.alertLevel === "high"
                  ? "bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400"
                  : card.hasAlert && card.alertLevel === "medium"
                  ? "bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400"
                  : "bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 group-hover:bg-gray-100 dark:group-hover:bg-gray-700"
              }`}>
                {card.icon}
              </div>
              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                <ArrowUpRight size={14} className="text-gray-400" />
              </div>
            </div>
            
            <div>
              <div className="flex items-center gap-2 mb-2">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {card.label}
                </p>
                {card.hasAlert && (
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    card.alertLevel === "high" 
                      ? "bg-red-500" 
                      : "bg-orange-500"
                  }`}></span>
                )}
              </div>
              <div className="flex items-baseline gap-2">
                <h3 className={`text-3xl font-semibold transition-colors ${
                  card.hasAlert && card.alertLevel === "high"
                    ? "text-red-700 dark:text-red-400"
                    : card.hasAlert && card.alertLevel === "medium"
                    ? "text-orange-700 dark:text-orange-400"
                    : "text-gray-900 dark:text-white"
                }`}>
                  {card.value}
                </h3>
                <span className={`flex items-center gap-1 text-xs font-medium ${
                  card.trendUp ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {card.trendUp ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                  {card.trend}
                </span>
              </div>
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">vs last 7 days</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Revenue Statistics */}
      {role !== "ROLE_EDITOR" && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Revenue Overview
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Track your revenue across different time periods
              </p>
            </div>
          </div>
          <RevenueStats />
        </motion.div>
      )}

      {/* Actionable Panels */}
      {role !== "ROLE_EDITOR" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Orders Snapshot */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            whileHover={{ scale: 1.005 }}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ClipboardList size={16} className="text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Orders Snapshot</h3>
              </div>
              {(pendingBookRequests > 0 || pendingBookReturnRequests > 0) && (
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                  {pendingBookRequests + pendingBookReturnRequests} total
                </span>
              )}
            </div>
            {(pendingBookRequests === 0 && pendingBookReturnRequests === 0 && booksWithUsersCount === 0) ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500 dark:text-gray-400">No active orders</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Orders will appear here</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div 
                  onClick={() => navigate("/adminOrderRequests")}
                  className={`group flex items-center justify-between p-3 rounded-lg transition-all cursor-pointer ${
                    pendingBookRequests > 0
                      ? "bg-orange-50 dark:bg-orange-500/10 hover:bg-orange-100 dark:hover:bg-orange-500/15"
                      : "bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {pendingBookRequests > 0 && (
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                    )}
                    <span className={`text-sm ${
                      pendingBookRequests > 0
                        ? "text-orange-700 dark:text-orange-400 font-medium"
                        : "text-gray-600 dark:text-gray-300"
                    }`}>Pending Orders</span>
                  </div>
                  <span className={`text-sm font-semibold ${
                    pendingBookRequests > 0
                      ? "text-orange-900 dark:text-orange-300"
                      : "text-gray-900 dark:text-white"
                  }`}>{getDisplayValue(pendingBookRequests)}</span>
                </div>
                <div 
                  onClick={() => navigate("/adminReturnRequests")}
                  className={`group flex items-center justify-between p-3 rounded-lg transition-all cursor-pointer ${
                    pendingBookReturnRequests > 0
                      ? "bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/15"
                      : "bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {pendingBookReturnRequests > 0 && (
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                    )}
                    <span className={`text-sm ${
                      pendingBookReturnRequests > 0
                        ? "text-red-700 dark:text-red-400 font-medium"
                        : "text-gray-600 dark:text-gray-300"
                    }`}>Returns Due</span>
                  </div>
                  <span className={`text-sm font-semibold ${
                    pendingBookReturnRequests > 0
                      ? "text-red-900 dark:text-red-300"
                      : "text-gray-900 dark:text-white"
                  }`}>{getDisplayValue(pendingBookReturnRequests)}</span>
                </div>
                <div 
                  onClick={() => navigate("/bookswithusers")}
                  className="group flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all cursor-pointer"
                >
                  <span className="text-sm text-gray-600 dark:text-gray-300">Books Out</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{getDisplayValue(booksWithUsersCount)}</span>
                </div>
              </div>
            )}
          </motion.div>

          {/* Subscriber Health */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.25 }}
            whileHover={{ scale: 1.005 }}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-200"
          >
            <div className="flex items-center gap-2 mb-4">
              <Users size={16} className="text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Subscriber Health</h3>
            </div>
            {(totalSubscribers === 0 && expiringSubscribersCount === 0 && expiredSubscribersCount === 0) ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500 dark:text-gray-400">No subscribers yet</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Subscriber data will appear here</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div 
                  onClick={() => navigate("/subscribed-users")}
                  className="group flex items-center justify-between p-3 bg-green-50 dark:bg-green-500/10 rounded-lg hover:bg-green-100 dark:hover:bg-green-500/20 border border-green-100 dark:border-green-500/20 hover:border-green-200 dark:hover:border-green-500/30 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-green-700 dark:text-green-400">Active</span>
                  </div>
                  <span className="text-sm font-semibold text-green-900 dark:text-green-300">{getDisplayValue(totalSubscribers)}</span>
                </div>
                <div 
                  onClick={() => navigate("/expiring-subscribers")}
                  className="group flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-500/10 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-500/20 border border-amber-100 dark:border-amber-500/20 hover:border-amber-200 dark:hover:border-amber-500/30 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                    <span className="text-sm text-amber-700 dark:text-amber-400">Expiring Soon</span>
                  </div>
                  <span className="text-sm font-semibold text-amber-900 dark:text-amber-300">{getDisplayValue(expiringSubscribersCount)}</span>
                </div>
                <div 
                  onClick={() => navigate("/expired-subscribers")}
                  className="group flex items-center justify-between p-3 bg-red-50 dark:bg-red-500/10 rounded-lg hover:bg-red-100 dark:hover:bg-red-500/20 border border-red-100 dark:border-red-500/20 hover:border-red-200 dark:hover:border-red-500/30 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="text-sm text-red-700 dark:text-red-400">Expired</span>
                  </div>
                  <span className="text-sm font-semibold text-red-900 dark:text-red-300">{getDisplayValue(expiredSubscribersCount)}</span>
                </div>
              </div>
            )}
          </motion.div>

          {/* Inventory Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
            whileHover={{ scale: 1.005 }}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-200"
          >
            <div className="flex items-center gap-2 mb-4">
              <Package size={16} className="text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Inventory Status</h3>
            </div>
            {totalBooks === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500 dark:text-gray-400">No books in inventory</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Add books to get started</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div 
                  onClick={() => navigate("/bookManager")}
                  className="group flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent transition-all cursor-pointer"
                >
                  <span className="text-sm text-gray-600 dark:text-gray-300">Total Books</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{getDisplayValue(totalBooks)}</span>
                </div>
                <div 
                  onClick={() => navigate("/bookswithusers")}
                  className="group flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent transition-all cursor-pointer"
                >
                  <span className="text-sm text-gray-600 dark:text-gray-300">In Circulation</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{getDisplayValue(booksWithUsersCount)}</span>
                </div>
                <div 
                  onClick={() => navigate("/bookManager")}
                  className="group flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent transition-all cursor-pointer"
                >
                  <span className="text-sm text-gray-600 dark:text-gray-300">Available</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {getDisplayValue(Math.max(0, totalBooks - booksWithUsersCount))}
                  </span>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Charts Section - Compact */}
      {role !== "ROLE_EDITOR" && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.35 }}
              whileHover={{ scale: 1.005 }}
            >
              <UserSubscriptionPie
                totalUsers={totalUsers}
                totalSubscribers={totalSubscribers}
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
              whileHover={{ scale: 1.005 }}
            >
              <PlanBreakdownPie planCounts={planCounts} />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.45 }}
              whileHover={{ scale: 1.005 }}
            >
              <PlanPackageBreakdownPie data={planPackageCounts} />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.5 }}
            whileHover={{ scale: 1.002 }}
          >
            <MostRentedBooks />
          </motion.div>
        </>
      )}
    </div>
  );
}
